'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.component';
import { Input } from '@/components/ui/input/input.component';
import { Link } from '@/components/ui/link/link.component';
import SelectCities from '@/components/ui/select-cities/select-cities.component';
import { useLoading } from '@/hooks/use-loading.hook';
import { City } from '@/types/city.type';
import { useToast } from '@/components/ui/use-toast';
import { userLocationStore } from '@/store/user-location.store';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';

const createCampaignFormSchema = z.object({
  city: z.string().nonempty('Digite sua cidade'),
  state: z.string(),
  email: z
    .string({
      message: 'Digite um e-mail válido',
    })
    .email({
      message: 'Digite um endereço de e-mail válido',
    }),
  acceptedTerms: z.boolean(),
  name: z.string(),
  isCampaign: z.boolean(),
});

export type CampaignForm = z.infer<typeof createCampaignFormSchema>;

export function CampaignForm() {
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const { isLoading, startLoading, stopLoading } = useLoading();
  const analytics = getAnalyticsPlugin();

  const { toast } = useToast();
  const defaultValues: CampaignForm = {
    email: '',
    city: '',
    state: '',
    name: '',
    acceptedTerms: true,
    isCampaign: true,
  };
  const form = useForm<CampaignForm>({
    resolver: zodResolver(createCampaignFormSchema),
    defaultValues,
    mode: 'all',
    delayError: 100,
  });

  useEffect(() => {
    if (selectedCity) {
      setHasInteracted(true);
      form.setValue('city', `${selectedCity.city}`);
      form.setValue('state', `${selectedCity.uf}`);
      form.trigger('city');
      form.trigger('state');
    } else if (hasInteracted) {
      form.setValue('city', '');
      form.setValue('state', '');
      form.trigger('city');
      form.trigger('state');
    }
  }, [form, selectedCity, hasInteracted]);

  async function onSubmit(data: CampaignForm): Promise<void> {
    startLoading();
    try {
      analytics.track(AnalyticsEnum.CLICK_SUBSCRIBE, {
        cidade: data.city,
        estado: data.state,
        email: data.email,
      });
      toast.success('Cadastro realizado com sucesso!');
    } catch (error) {
      const logger = getLogger('CampaignFormOnSubmit');
      logger.warn('Error while submiting the campaign form', { error });
      toast.error('Erro ao enviar informações. Verifique sua conexão e tente novamente.');
    } finally {
      form.reset(defaultValues);
      setSelectedCity(null);
      setHasInteracted(false);
      stopLoading();
    }
  }

  const userLocation = useStore(
    userLocationStore,
    (state) => state.userLocation,
  );

  const { setValue, trigger } = form;

  useEffect(() => {
    if (userLocation) {
      setValue('city', userLocation.city);
      setValue('state', userLocation.state);
      setSelectedCity({ city: userLocation.city, uf: userLocation.state });
      trigger(['city', 'state']);
    }
  }, [userLocation, setValue, trigger]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        aria-label="form"
        className="space-y-6"
      >
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Cidade
              </FormLabel>
              <FormControl>
                <SelectCities
                  {...field}
                  selectedCity={selectedCity}
                  onChange={setSelectedCity}
                  hasError={!!form.formState.errors.city}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                E-mail
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Digite seu e-mail"
                  shouldClearValue
                  {...field}
                  className=" !text-clip"
                  autoComplete="off"
                  data-testid="input-email--campaign-form"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div
          className="text-sm text-gray-500 "
          data-testid={DataTestIdEnum.BLOG_CAMPAIGN_TERMS_SECTION}
        >
          <span>Ao se inscrever você concorda com os </span>
          <Link prefetch={false} href="/termos-de-uso/">
            <span
              className="text-green-500 cursor-pointer font-bold"
              data-testid="span-terms--register-form"
            >
              Termos de Uso
            </span>
          </Link>
          <span> e </span>
          <Link prefetch={false} href="/politica-de-privacidade/">
            <span
              className="text-green-500 cursor-pointer font-bold"
              data-testid="span-policy--register-form"
            >
              Política de Privacidade.
            </span>
          </Link>
        </div>
        <Button
          data-testid="button-submit--campaign-form"
          type="submit"
          variant="default"
          color="primary"
          className="w-full h-12"
          disabled={!form.formState.isValid}
          isLoading={isLoading}
        >
          Quero me inscrever
        </Button>
      </form>
    </Form>
  );
}
